import { useMutation, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useMemo } from 'react';

import {
	getPreferences,
	putPreferences,
	PutPreferencesRequest,
} from 'services/account';

import { useErrorHandler } from 'hooks/use-error-handler';
import { useToast } from 'hooks/use-toast';

import stylesDefault from '@myaccount/pages/styles.module.scss';

const PreferencesForm = lazy(
	() => import('@myaccount/patterns/preferences-form/preferences-form')
);
const Loading = lazy(() => import('core/generic/elements/loading'));

export default function Preferences() {
	const toast = useToast();
	const errorHandler = useErrorHandler();

	const query = useQuery({
		queryKey: ['preferences'],
		queryFn: () => getPreferences().then((response) => response.data),
	});

	const preferences = useMemo(() => {
		return query.data?.subscriptions || undefined;
	}, [query.data?.subscriptions]);

	const disclaimerText = useMemo(() => {
		return query.data?.data?.disclaimerText || undefined;
	}, [query.data?.data?.disclaimerText]);

	const mutation = useMutation({
		mutationKey: ['preferences'],
		mutationFn: async (payload: PutPreferencesRequest) =>
			await putPreferences(payload),
		onError(error) {
			errorHandler.process(error, false);
		},
		onSuccess() {
			toast.success({ content: 'Changes successfully saved' });
			query.refetch();
		},
	});

	if (query.isLoading) {
		return (
			<Suspense>
				<div className={stylesDefault.loading}>
					<Loading dimension="large" isWithCrest />
				</div>
			</Suspense>
		);
	}

	return (
		<Suspense>
			<PreferencesForm
				preferences={preferences}
				disclaimerText={disclaimerText}
				isSubmitting={mutation.isPending}
				onSubmit={mutation.mutateAsync}
			/>
		</Suspense>
	);
}
